import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from '@reduxjs/toolkit'

import { ConversationType, SessionV2 } from "interfaces/db"
import { useLiveStatus } from "components/LiveListen/hooks/useLiveStatus"
import { Message, MessageType } from "interfaces/relay"
import { getServicesManager } from "services"
import { SUBSCRIBE_CLIENT_ENDPOINT } from "cfg/endpoints"
import { addMessage, TextMessage } from "lib/redux/sessions/texting/slice"
import { RootState } from "store"
import { cleanNumber } from "components/SessionHistory/number-helpers"
import { ca } from "date-fns/locale"

export function useNewTextSessions() {
    const [textSessions, setTextSessions] = useState<{session: SessionV2, isOver: boolean}[]>([])

    // Always create the callback, but make its effect conditional
    const handleMessage = useCallback((message: Message) => {
        switch (message.message_type) {
            case MessageType.SESSION_STATUS: {
                if (message.session.conversation_type !== ConversationType.MESSAGING) return
                setTextSessions(prevSessions => {
                    // if the session exists, update its status
                    const existingIndex = prevSessions.findIndex(s => s.session.session_id === message.session.session_id)
                    if (existingIndex !== -1) {
                        // Only create new array if status actually changed
                        if (prevSessions[existingIndex].isOver === (message.metadata.ended_at !== null)) {
                            return prevSessions
                        }
                        const newSessions = [...prevSessions]
                        newSessions[existingIndex] = {
                            session: message.session,
                            isOver: message.metadata.ended_at !== null
                        }
                        return newSessions
                    }
                    // if it's a new session, add it
                    return [...prevSessions, {
                        session: message.session,
                        isOver: message.metadata.ended_at !== null
                    }]
                })
                break
            }
        }
    }, [])


    const can_text = useSelector((state: RootState) => state.team.can_text)
    useLiveStatus(handleMessage, {abort: !can_text})

    return textSessions
}

export function useTexts() {
    const can_text = useSelector((state: RootState) => state.team.can_text)
    const textSessions = useNewTextSessions()
    const activeSubscriptions = useRef<Map<string, WebSocket | null>>(new Map())
    const dispatch = useDispatch()
    
    // Memoize the session IDs to prevent unnecessary effect runs
    const sessionIds = useMemo(() => new Set(textSessions.map(s => s.session.session_id)), [textSessions])
    
    useEffect(() => {
        if (!can_text) {
            // Clean up any existing subscriptions if texting is disabled
            for (const ws of activeSubscriptions.current.values()) {
                if (ws) ws.close()
            }
            activeSubscriptions.current.clear()
            return
        }

        // Clean up subscriptions for sessions that no longer exist
        for (const [sessionId, ws] of activeSubscriptions.current.entries()) {
            if (!sessionIds.has(sessionId)) {
                if (ws) ws.close()
                activeSubscriptions.current.delete(sessionId)
            }
        }
        
        // Create new subscriptions for sessions that don't have them
        textSessions.forEach(({ session, isOver }) => {
            const sessionId = session.session_id
            if (!activeSubscriptions.current.has(sessionId)) {
                const ws = new WebSocket(`wss://${session.base_path}/${SUBSCRIBE_CLIENT_ENDPOINT}?session_id=${sessionId}&inc_transcript=true&backfill_transcript=true`)
                ws.binaryType = "arraybuffer"
                ws.onopen = () => { getServicesManager().getApiKey().then((v) => ws.send(JSON.stringify({'api_key': v}))) }
                ws.onmessage = (event) => {
                    if (typeof(event.data) === 'string') {
                        const data = JSON.parse(event.data)
                        if (!data.syndicate_type || !data.message) return
                        if (data.syndicate_type !== 'MESSAGE' || !data.message) return
                        const newMessage: TextMessage = {
                            sessionId: sessionId,
                            body: data.message.body,
                            fromAddress: data.message.from_address,
                            sentAt: data.message.sent_at,
                            messageId: data.message.message_id,
                            delivered: data.message.delivered,
                            deliverySetAt: data.message.delivery_set_at
                        }
                        dispatch(addMessage({session: session, message: newMessage}))
                    }
                }
                activeSubscriptions.current.set(sessionId, ws)
            }
        })
    }, [sessionIds, dispatch, can_text])
    
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            for (const ws of activeSubscriptions.current.values()) {
                if (ws) ws.close()
            }
            activeSubscriptions.current.clear()
        }
    }, [])
}

function normalizeNumber(number: string) {
    const cleaned = cleanNumber(number)
    if (cleaned.length === 10) return `1${cleaned}`
    return cleaned
}

// Convert to custom hook
export function useTextsForNumbers(numbers: string[]) {
    // Memoize the normalized numbers to prevent unnecessary selector recreation
    const normalizedNumbers = useMemo(() => 
        numbers.map(normalizeNumber), 
        [numbers.join(',')]
    )
    
    // Create memoized selector using createSelector
    const selectTexts = useMemo(() => 
        createSelector(
            (state: RootState) => state.textingSessions.numberToMessages,
            (state: RootState) => normalizedNumbers,
            (texts, numbers) => numbers.flatMap(n => texts[n] || [])
        ), 
        [normalizedNumbers]
    )
    
    return useSelector(selectTexts)
}